// src/components/common/ProductCard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Info } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import type { FirebaseProduct } from '../../types/product';

interface ProductCardProps {
  product: FirebaseProduct;
  onAddToCart: (product: FirebaseProduct) => void;
}

const formatPrice = (price: number | undefined | null): string => {
  if (typeof price !== 'number' || price === 0) return 'To Order';
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(price);
};

const StockBadge: React.FC<{ stock: number }> = ({ stock }) => {
  if (stock === 0) {
    return (
      <div className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-sm">
        Out of Stock
      </div>
    );
  }
  if (stock < 20) {
    return (
      <div className="absolute top-2 right-2 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-medium shadow-sm">
        Low Stock
      </div>
    );
  }
  return null;
};

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const isPriceOnRequest = !product.price || product.price === 0;
  const hasSpecifications = Object.values(product.specifications).some(value => value);

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
      {/* Image Section */}
      <Link to={`/product/${product.slug}`} className="block relative">
        <div className="aspect-square bg-gray-50 rounded-t-xl overflow-hidden p-6">
          <img
            src={product.images[0]?.url || '/api/placeholder/400/400'}
            alt={product.images[0]?.alt || product.name}
            className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
          />
          <StockBadge stock={product.stock} />
        </div>
      </Link>

      {/* Content Section */}
      <div className="p-6 flex flex-col flex-grow">
        {/* Brand and Rating */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-brand-blue-500">{product.brand}</p>
          {product.rating && (
            <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-full">
              <Star className="h-4 w-4 text-yellow-400 fill-current" />
              <span className="ml-1 text-sm font-medium text-gray-700">{product.rating}</span>
            </div>
          )}
        </div>

        {/* Product Title */}
        <Link to={`/product/${product.slug}`}>
          <h3 className="text-lg font-semibold text-gray-900 hover:text-brand-blue-500 
            transition-colors mb-2 line-clamp-2">
            {product.name}
          </h3>
        </Link>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>

        {/* Specifications */}
        {hasSpecifications && (
          <div className="space-y-1 mb-4">
            {Object.entries(product.specifications).map(([key, value]) => (
              value && (
                <p key={key} className="text-sm text-gray-600 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-brand-blue-100 mr-2"></span>
                  <span className="capitalize font-medium">{key}:</span>
                  <span className="ml-1">{value}</span>
                </p>
              )
            ))}
          </div>
        )}

        {/* Price and Actions Section */}
        <div className="mt-auto pt-4 border-t">
          <div className="flex items-center justify-between">
            {isPriceOnRequest ? (
              <div className="flex items-center text-brand-blue-500">
                <Info className="h-5 w-5 mr-1" />
                <span className="font-medium">To Order</span>
              </div>
            ) : (
              <span className="text-xl font-bold text-gray-900">
                {formatPrice(product.price)}
              </span>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onAddToCart(product)}
                disabled={product.stock === 0}
                className={`p-2 rounded-lg transition-colors flex items-center justify-center
                  ${product.stock === 0
                    ? 'bg-gray-100 cursor-not-allowed text-gray-400'
                    : 'bg-brand-blue-500 text-white hover:bg-brand-blue-600 shadow-sm'
                  }`}
                title={product.stock === 0 ? 'Out of stock' : 'Add to cart'}
              >
                <ShoppingCart className="h-5 w-5" />
              </button>
              <a
                href={`https://wa.me/254706788802?text=Hi, I'm interested in ${encodeURIComponent(product.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-green-500 hover:bg-green-600 text-white 
                  transition-colors shadow-sm flex items-center justify-center"
                title="Inquire on WhatsApp"
              >
                <FaWhatsapp className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Stock Warning */}
          {product.stock > 0 && product.stock < 20 && (
            <p className="text-sm text-yellow-600 mt-2 flex items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 mr-1"></span>
              Only {product.stock} left in stock
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;